import { DeleteOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { theme } from 'antd';
import { useManagePermittedRoles } from 'library/hooks';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { organisationActions } from 'reduxStore/organisations/slice';
import { Alert, Button, Flex, Form, Input, Modal, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const DeleteOrganisationModal = ({ children, visible: visibleProp, setVisible: setVisibleProp }) => {
	// ✅ Hooks
	const {
		token: { paddingXXL = 48, borderRadiusLG, paddingXS, colorText, paddingSM, paddingLG, marginLG }
	} = theme.useToken();
	const dispatch = useDispatch();
	const [form] = Form.useForm();
	const organisationName = Form.useWatch('organisationName', form);

	//✅ Redux Selectors
	const {
		selectedProfile: { orgID }
	} = useSelector((state) => state.Auth);
	const { data: organisations, deleteOrganisationLoading } = useSelector((state) => state.Organisations);

	//✅  Permissions
	const canDeleteOrg = useManagePermittedRoles('deleteOrg');
	const currentOrganisation = useMemo(
		() => organisations?.find((org) => org.orgID === orgID),
		[organisations, orgID]
	);

	// ✅ Local State
	const [internalVisible, setInternalVisible] = useState(false);
	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;

	const isFormValid = organisationName?.trim() === currentOrganisation?.name;

	// ✅ Close modal
	const handleCancel = useCallback(() => {
		setVisible(false);
		form.resetFields();
	}, [setVisible, form]);

	// ✅ Handle delete action
	const handleDelete = () => {
		dispatch(organisationActions.deleteOrganisation({ deletedOrgID: orgID }));
	};

	return (
		<Fragment>
			<Tooltip
				title={
					!canDeleteOrg ? "You don't have permission to delete this organisation" : 'Delete this Organisation'
				}
				placement='top'
			>
				<span onClick={() => canDeleteOrg && setVisible(true)}>
					{children ?? (
						<Button icon={<DeleteOutlined />} disabled={!canDeleteOrg} danger type='primary'>
							Delete
						</Button>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				onCancel={() => {
					if (deleteOrganisationLoading) return;
					handleCancel();
				}}
				closeIcon={false}
				centered
				destroyOnClose
				footer={[
					<Button onClick={handleCancel} type='default' disabled={deleteOrganisationLoading}>
						Cancel
					</Button>,
					<Button
						onClick={handleDelete}
						type='primary'
						danger
						disabled={!isFormValid}
						loading={deleteOrganisationLoading}
					>
						Delete Organisation
					</Button>
				]}
				width={750}
				styles={{
					content: { padding: paddingXXL, borderRadius: borderRadiusLG },
					footer: {
						marginTop: marginLG
					}
				}}
			>
				<Flex vertical align='center' justify='center' gap={paddingLG}>
					<Flex gap={paddingXS} vertical align='center'>
						<Title
							style={{
								margin: 0,
								lineHeight: '32px'
							}}
							level={3}
						>
							Delete this organisation?
						</Title>

						<Title
							style={{
								margin: 0,
								lineHeight: '46px'
							}}
							level={1}
						>
							{currentOrganisation?.name || 'Unknown Organisation'}
						</Title>
					</Flex>

					<Alert
						message='This action CANNOT be undone. This will permanently remove all associated data, including users, teams, devices, device groups, credentials, and other records.'
						type='error'
						icon={<ExclamationCircleFilled />}
						showIcon
						closable={false}
						style={{
							padding: `${paddingXS}px ${paddingSM}px`,
							fontWeight: 500
						}}
					/>

					<Form form={form} layout='vertical' preserve={false} style={{ margin: 0 }}>
						<Flex vertical gap={paddingXS}>
							<Text
								style={{
									color: colorText,
									fontWeight: 500,
									lineHeight: '22px'
								}}
							>
								Please type the name of your organisation to confirm you understand and acknowledge this
								action.
							</Text>

							<Form.Item
								name='organisationName'
								rules={[
									{
										validator: (_, value) => {
											if (!value || value.trim() === currentOrganisation?.name) {
												return Promise.resolve();
											}
											return Promise.reject(
												new Error(
													`Please enter the exact organisation name: "${currentOrganisation?.name}"`
												)
											);
										}
									}
								]}
								style={{ margin: 0 }}
							>
								<Input
									type='text'
									placeholder={`Enter "${currentOrganisation?.name || 'Organisation Name'}"`}
									autoComplete='off'
								/>
							</Form.Item>
						</Flex>
					</Form>
				</Flex>
			</Modal>
		</Fragment>
	);
};

export default DeleteOrganisationModal;
